import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Row,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Alert,
} from "antd";
import {
  PlusOutlined,
  RobotOutlined,
  CopyOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { listAgents, createAgent, revokeAgent } from "../../../service/agent.service";
import { getAllEnvironment } from "../../../service/admin.service";
import PageHeader from "../../../components/generic/PageHeader";
import "../../../styles/db.css";

const { Option } = Select;
const { Paragraph } = Typography;

function Agents() {
  const [agents, setAgents] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState(null); // { name, environment, key }
  const [form] = Form.useForm();

  // Environment names can repeat in the library; dedupe so the Select has unique
  // keys/values (duplicate keys make AntD's virtual dropdown duplicate rows on
  // scroll). An agent's environment is just a routing string, so identical names
  // are interchangeable.
  const envOptions = useMemo(
    () => [
      ...new Set((environments || []).map((e) => e.displayName).filter(Boolean)),
    ],
    [environments],
  );

  const fetchAgents = () => {
    setLoading(true);
    listAgents()
      .then((res) => setAgents(res.data || []))
      .catch(() => message.error("Failed to load agents"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAgents();
    getAllEnvironment()
      .then((res) => setEnvironments(res.data || []))
      .catch(() => setEnvironments([]));
  }, []);

  const openCreate = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const handleCreate = () => {
    form.validateFields().then((values) => {
      setSaving(true);
      createAgent(values.name, values.environment)
        .then((res) => {
          setModalOpen(false);
          // Raw key is returned ONCE — surface it for the user to copy.
          setNewKey({
            name: values.name,
            environment: values.environment,
            key: res.data.key,
          });
          fetchAgents();
        })
        .catch(() => message.error("Failed to create agent"))
        .finally(() => setSaving(false));
    });
  };

  const handleRevoke = (agentId) => {
    revokeAgent(agentId)
      .then(() => {
        message.success("Agent revoked");
        fetchAgents();
      })
      .catch(() => message.error("Failed to revoke agent"));
  };

  const copyKey = () => {
    if (!newKey?.key) return;
    navigator.clipboard
      .writeText(newKey.key)
      .then(() => message.success("Key copied to clipboard"))
      .catch(() => message.error("Could not copy — select and copy manually"));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (text) => (
        <span>
          <RobotOutlined style={{ marginRight: 6, color: "#185FA5" }} />
          {text}
        </span>
      ),
    },
    {
      title: "Environment",
      dataIndex: "environment",
      key: "environment",
      width: "16%",
      render: (text) =>
        text ? <Tag color="geekblue">{text}</Tag> : <Tag>any</Tag>,
    },
    {
      title: "Last Seen",
      dataIndex: "lastSeen",
      key: "lastSeen",
      width: "18%",
      render: (text) =>
        text ? (
          <span style={{ fontSize: 12 }}>{text}</span>
        ) : (
          <span style={{ color: "#bbb", fontSize: 12 }}>Never connected</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: "12%",
      render: (active) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Active" : "Revoked"}
        </Tag>
      ),
      filters: [
        { text: "Active", value: true },
        { text: "Revoked", value: false },
      ],
      onFilter: (value, record) => record.active === value,
    },
    {
      title: "Created",
      dataIndex: "createdDate",
      key: "createdDate",
      width: "16%",
      defaultSortOrder: "descend",
      sorter: (a, b) =>
        (a.createdDate || "").localeCompare(b.createdDate || ""),
      render: (text) => <span style={{ fontSize: 12 }}>{text}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: "12%",
      render: (_, record) =>
        record.active ? (
          <Popconfirm
            title="Revoke this agent?"
            description="Its key stops authenticating immediately. The VM will need a new key."
            okText="Revoke"
            okType="danger"
            cancelText="Cancel"
            onConfirm={() => handleRevoke(record.agentId)}
          >
            <Button size="small" danger>
              Revoke
            </Button>
          </Popconfirm>
        ) : (
          <span style={{ color: "#bbb", fontSize: 12 }}>—</span>
        ),
    },
  ];

  return (
    <div className="db-container">
      <PageHeader
        backTo="/admin"
        icon={<RobotOutlined />}
        title="Agents"
        tooltip="On-prem automation agents (one per Windows/Citrix VM) that run desktop automation suites"
        actions={
          <Button className="db-add" icon={<PlusOutlined />} onClick={openCreate}>
            New Agent
          </Button>
        }
      />

      <Row className="table-container">
        <Table
          columns={columns}
          dataSource={agents}
          rowKey="agentId"
          loading={loading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Row>

      {/* ─── Create agent modal ─────────────────────────────── */}
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title="New Agent"
        onOk={handleCreate}
        okText="Create"
        confirmLoading={saving}
        destroyOnClose
        centered
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Agent Name"
            rules={[{ required: true, message: "Agent name is required" }]}
            extra={
              <span style={{ fontSize: 11, color: "#888" }}>
                A label to identify the VM, e.g. <code>CRIMS-PROD-VM01</code>
              </span>
            }
          >
            <Input placeholder="e.g. CRIMS-PROD-VM01" />
          </Form.Item>
          <Form.Item
            name="environment"
            label="Environment"
            rules={[{ required: true, message: "Environment is required" }]}
            extra={
              <span style={{ fontSize: 11, color: "#888" }}>
                The agent only claims Citrix runs queued for this environment.
              </span>
            }
          >
            <Select
              showSearch
              placeholder="Select environment"
              optionFilterProp="children"
            >
              {envOptions.map((name) => (
                <Option key={name} value={name}>
                  {name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ─── One-time key reveal modal ──────────────────────── */}
      <Modal
        open={!!newKey}
        onCancel={() => setNewKey(null)}
        title={
          <span>
            <KeyOutlined style={{ marginRight: 8, color: "#3B6D11" }} />
            Agent key — copy it now
          </span>
        }
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={copyKey}>
            Copy
          </Button>,
          <Button key="done" type="primary" onClick={() => setNewKey(null)}>
            Done
          </Button>,
        ]}
        centered
        width={560}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This key is shown only once"
          description="ARC stores only a hash of it — there's no way to see it again. Paste it into the agent VM's agent.properties now. If lost, revoke and create a new agent."
        />
        {newKey && (
          <>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
              {newKey.name} · {newKey.environment}
            </div>
            <Paragraph
              copyable={{ text: newKey.key }}
              code
              style={{
                wordBreak: "break-all",
                background: "var(--color-background-secondary, #f5f7fa)",
                padding: "10px 12px",
                borderRadius: 6,
                margin: 0,
              }}
            >
              {newKey.key}
            </Paragraph>
            <div style={{ fontSize: 12, color: "#888", marginTop: 12 }}>
              In <code>agent.properties</code>:
              <pre
                style={{
                  background: "var(--color-background-secondary, #f5f7fa)",
                  padding: "8px 12px",
                  borderRadius: 6,
                  marginTop: 6,
                  fontSize: 12,
                }}
              >{`arc.url=https://www.archub.com.au
arc.agent.key=${newKey.key}`}</pre>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default Agents;
